import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useUserStore } from '@/hooks/useUserStore';
import {
  useGetAllFoodQueryV2,
  useGetRankQueryV2,
} from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';
import { useBootup } from '@/hooks/useBootup'; // Import useBootup for timestamp
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from 'lucide-react';
import { NUTRITION_PATH } from '@/constants/paths';
// Assuming these types exist and have at least these properties based on analysis
// Actual types might be more complex and reside in @viome/viome-base/src/usecase/Nutrition/Nutrition.model.ts
interface FoodData {
  id: number | string; // This is the viome_food_id
  title: string; // Food name (for logging, not used as key anymore)
  rank: number | string; // This is the rank_id (needs mapping to rank name)
  // ... other properties if any
}
// Assuming useGetAllFoodQueryV2 returns FoodData[] or similar structure
// type GetAllFoodsResponse = FoodData[]; // Type is implicitly from the hook

interface RankItem {
  id: number | string; // Rank ID
  title: string; // Rank name (e.g., "Enjoy", "Minimize")
  // ... other properties if any
}

const RECIPES_IFRAME_BASE_URL = 'https://recipes.viome.com/';

/**
 * RecipesPage Component with PostMessage Iframe Navigation & DEBUG LOGGING
 * 
 * This component implements iframe back navigation using the postMessage API
 * for secure cross-origin communication between beta-experience.viome.com and recipes.viome.com
 * 
 * IFRAME LOADING:
 * - Loads recipes.viome.com with personalized data and embed=true for optimal iframe experience
 * 
 * POSTMESSAGE NAVIGATION:
 * 1. Sends 'VIOME_NAVIGATE_BACK' message to iframe
 * 2. Waits for 'VIOME_NAVIGATION_RESPONSE' from iframe
 * 3. If response.success = true: stays in iframe (iframe navigated)
 * 4. If response.success = false: navigates to main app (iframe at beginning)
 * 5. If no response in 1 second: timeout fallback to main app
 * 
 * DEBUG LOGGING ENABLED:
 * - All console logs are prefixed with emojis for easy filtering
 * - Look for "=== BACK NAVIGATION DEBUG START/END ===" blocks
 * - PostMessage attempts with 📨
 * - Navigation detection with ⏰
 * - Success/failure states with ✅/❌
 * 
 * REQUIRED POSTMESSAGE IMPLEMENTATION IN RECIPES.VIOME.COM:
 * 
 * Add this JavaScript code to recipes.viome.com to handle back navigation:
 * 
 *    ```javascript
 *    // Listen for messages from the parent app
 *    window.addEventListener('message', (event) => {
 *      // Security: Only accept messages from the main Viome app
 *      if (event.origin !== 'https://beta-experience.viome.com') {
 *        console.log('Rejected message from unknown origin:', event.origin);
 *        return;
 *      }
 *      
 *      // Handle back navigation requests
 *      if (event.data?.type === 'VIOME_NAVIGATE_BACK') {
 *        console.log('Received back navigation request from parent');
 *        
 *        // Check if we can go back in our history
 *        const canGoBack = window.history.length > 1;
 *        console.log('History length:', window.history.length, 'Can go back:', canGoBack);
 *        
 *        if (canGoBack) {
 *          // We have history to go back to
 *          window.history.back();
 *          
 *          // Tell parent we successfully navigated back
 *          event.source.postMessage({
 *            type: 'VIOME_NAVIGATION_RESPONSE',
 *            success: true,
 *            message: 'Successfully navigated back in iframe history'
 *          }, event.origin);
 *        } else {
 *          // We're at the beginning - tell parent to handle navigation
 *          event.source.postMessage({
 *            type: 'VIOME_NAVIGATION_RESPONSE',
 *            success: false,
 *            message: 'At beginning of history, parent should navigate back to main app'
 *          }, event.origin);
 *        }
 *      }
 *    });
 *    
 *    // OPTIONAL: Notify parent when URL changes (for better referer tracking)
 *    function notifyParentOfUrlChange(newUrl) {
 *      try {
 *        window.parent.postMessage({
 *          type: 'URL_CHANGE',
 *          url: newUrl || window.location.href
 *        }, 'https://beta-experience.viome.com');
 *      } catch (error) {
 *        console.log('Failed to notify parent of URL change:', error);
 *      }
 *    }
 *    ```
 */
export const RecipesPage: React.FC = () => {
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { user: userDetails } = useUserStore();
  const { data: allFoods, isLoading: isLoadingFoods, error: errorFoods } = useGetAllFoodQueryV2();
  const { data: allRanks, isLoading: isLoadingRanks, error: errorRanks } = useGetRankQueryV2();
  const { bootupState, isLoading: isLoadingBootup, isError: isErrorBootup } = useBootup(); // Use useBootup

  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [currentReferer, setCurrentReferer] = useState<string>('recipes');
  const [isWaitingForBackResponse, setIsWaitingForBackResponse] = useState<boolean>(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState<boolean>(false);
  const backResponseTimeoutRef = useRef<NodeJS.Timeout | null>(null);


  // Debug: Track iframe src changes
  useEffect(() => {
    if (iframeSrc) {
      setIsIframeLoaded(false); // Reset loaded state when src changes
    } 
  }, [iframeSrc]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (backResponseTimeoutRef.current) {
        clearTimeout(backResponseTimeoutRef.current);
      }
    };
  }, []);

  // Get current referer URL for navigation
  const getCurrentReferer = useCallback((): string => {
    try {
      // Try to get current URL from iframe (will likely fail due to cross-origin)
      if (iframeRef.current?.contentWindow?.location?.href) {
        return iframeRef.current.contentWindow.location.href;
      }
    } catch {
      // Cross-origin restriction expected
    }
    
    // Fallback to tracked referer or initial iframe src
    return currentReferer === 'recipes' ? (iframeSrc || 'recipes.viome.com') : currentReferer;
  }, [currentReferer, iframeSrc]);

  // Handle iframe load event
  const handleIframeLoad = () => {
    console.log('🎯 Iframe loaded successfully');
    setIsIframeLoaded(true);
    console.log('Current iframe state:', {
      src: iframeRef.current?.src,
      contentWindow: !!iframeRef.current?.contentWindow,
      readyState: iframeRef.current?.contentDocument?.readyState
    });
  };

  // Listen for postMessage events from iframe to track current page and handle navigation
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('📨 PostMessage received:', {
        origin: event.origin,
        data: event.data,
        timestamp: new Date().toISOString()
      });
      
      // Only accept messages from recipes.viome.com domain
      if (event.origin !== 'https://recipes.viome.com') {
        console.log('❌ Message rejected - wrong origin. Expected: https://recipes.viome.com, Got:', event.origin);
        return;
      }
      
      console.log('✅ Message origin verified, processing...');
      
      if (event.data?.type === 'URL_CHANGE' && event.data?.url) {
        console.log('📍 URL_CHANGE received from iframe:', event.data.url);
        setCurrentReferer(event.data.url);
      }
      
      // Handle back navigation responses
      if (event.data?.type === 'VIOME_NAVIGATION_RESPONSE') {
        console.log('📨 VIOME_NAVIGATION_RESPONSE received:', {
          success: event.data?.success,
          message: event.data?.message,
          currentWaitingState: isWaitingForBackResponse,
          hasTimeout: !!backResponseTimeoutRef.current
        });
        
        // Clear the timeout since we got a response
        if (backResponseTimeoutRef.current) {
          console.log('⏰ Clearing timeout due to response');
          clearTimeout(backResponseTimeoutRef.current);
          backResponseTimeoutRef.current = null;
        }
        
        setIsWaitingForBackResponse(false);
        console.log('✅ Set isWaitingForBackResponse to false');
        
        if (event.data?.success === false) {
          // iframe is at the beginning and can't go back further
          console.log('🚫 iframe reports navigation failed - navigating to nutrition page');
          console.log('📝 iframe message:', event.data?.message);
          const refererUrl = getCurrentReferer();
          navigate(NUTRITION_PATH, { 
            state: { 
              referer: refererUrl,
              timestamp: new Date().toISOString()
            } 
          });
          console.log('=== BACK NAVIGATION DEBUG END (IFRAME AT BEGINNING) ===');
        } else {
          // iframe successfully went back, stay in iframe
          console.log('✅ iframe successfully navigated back - staying in webview');
          console.log('📝 iframe message:', event.data?.message);
          console.log('=== BACK NAVIGATION DEBUG END (SUCCESS) ===');
        }
      }
      
      // Log any other message types for debugging
      if (event.data?.type && !['URL_CHANGE', 'VIOME_NAVIGATION_RESPONSE'].includes(event.data.type)) {
        console.log('❓ Unknown message type received:', event.data.type, event.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, getCurrentReferer, isWaitingForBackResponse, iframeSrc]);

  // Handle back navigation - try direct iframe navigation first
  const handleBackNavigation = () => {
    const refererUrl = getCurrentReferer();
    
    console.log('=== BACK NAVIGATION DEBUG START ===');
    console.log('Back button clicked, current state:', {
      refererUrl,
      isWaitingForBackResponse,
      iframeSrc,
      iframeExists: !!iframeRef.current,
      iframeContentWindow: !!iframeRef.current?.contentWindow,
      currentReferer,
      isIframeLoaded
    });
    
    // If we're already waiting for a response, don't send another request
    if (isWaitingForBackResponse) {
      console.log('❌ Already waiting for back response, ignoring click');
      console.log('=== BACK NAVIGATION DEBUG END ===');
      return;
    }
    
    if (iframeRef.current?.contentWindow && iframeSrc) {
      try {
        console.log('📨 Sending postMessage to iframe for back navigation...');
        
        // Send postMessage to iframe requesting back navigation
        iframeRef.current.contentWindow.postMessage({
          type: 'VIOME_NAVIGATE_BACK',
          source: 'viome_parent_app',
          timestamp: new Date().toISOString()
        }, 'https://recipes.viome.com');
        
        console.log('✅ PostMessage sent to recipes.viome.com');
        setIsWaitingForBackResponse(true);
        
        // Set timeout as fallback - if iframe doesn't respond, go to main app
        backResponseTimeoutRef.current = setTimeout(() => {
          console.log('⏰ TIMEOUT: No response from iframe after 1 second');
          console.log('Executing timeout fallback navigation...');
          
          // Clear the waiting state and navigate back to main app
          setIsWaitingForBackResponse(false);
          navigate(NUTRITION_PATH, { 
            state: { 
              referer: refererUrl,
              timestamp: new Date().toISOString()
            } 
          });
          console.log('✅ Navigated to nutrition page via timeout fallback');
          console.log('=== BACK NAVIGATION DEBUG END (TIMEOUT FALLBACK) ===');
        }, 1000); // 1 second timeout
        
        console.log('✅ Timeout set with ID:', backResponseTimeoutRef.current);
        
      } catch (error) {
        console.log('❌ EXCEPTION: Failed to send postMessage to iframe', error);
        console.log('Falling back to main app navigation');
        navigate(NUTRITION_PATH, { 
          state: { 
            referer: refererUrl,
            timestamp: new Date().toISOString()
          } 
        });
        console.log('=== BACK NAVIGATION DEBUG END (EXCEPTION) ===');
      }
    } else {
      // No iframe available, navigate back to nutrition
      console.log('❌ No iframe available, conditions check:', {
        iframeRef: !!iframeRef.current,
        contentWindow: !!iframeRef.current?.contentWindow,
        iframeSrc: !!iframeSrc,
        isIframeLoaded
      });
      console.log('Navigating to nutrition immediately');
      navigate(NUTRITION_PATH, { 
        state: { 
          referer: refererUrl,
          timestamp: new Date().toISOString()
        } 
      });
      console.log('=== BACK NAVIGATION DEBUG END (NO IFRAME) ===');
    }
  };

  useEffect(() => {
    if (isLoadingFoods || isLoadingRanks || isLoadingBootup) {
      return; // Wait for all queries to finish
    }

    const combinedError = errorFoods || errorRanks || isErrorBootup;
    if (combinedError) {
      console.error('Error fetching data:', { errorFoods, errorRanks, isErrorBootup });
      setDataError('Failed to load recipe personalization data. Please try again later.');
      setIframeSrc(null);
      return;
    }

    if (allFoods && allRanks && userDetails && userDetails?.userID && bootupState) {
      try {
        const rankMap = allRanks.reduce((acc: Record<string, string>, rank: RankItem) => {
          acc[String(rank.id)] = rank.title;
          return acc;
        }, {});

        const scoresById: Record<string, string> = {};
        // Explicitly cast allFoods if its inferred type from the hook is too generic or doesn't match FoodData
        // For now, assuming items in allFoods have .id and .rank (as rank_id)
        (allFoods as FoodData[]).forEach((food: FoodData) => {
          const rankName = rankMap[String(food.rank)]; // food.rank is the rank_id
          if (food.id && rankName) { // food.id is the viome_food_id
            scoresById[String(food.id)] = rankName;
          } else {
            console.warn(`Missing rank name for food ID: ${food.id} (rank_id: ${food.rank}) or food title: ${food.title}`);
          }
        });

        // Use timestamp from bootupState.rnrLastGeneratedDate if available, otherwise current time.
        const payloadTimestamp = bootupState.rnrLastGeneratedDate || new Date().toISOString();

        const payload = {
          userId: userDetails.userID, // Already a string from useUserDetails
          firstName: userDetails.firstName, // Include firstName from credentials response
          scoresById, // Use scoresById with food IDs as keys
          timestamp: payloadTimestamp,
        };

        const encodedPayload = btoa(JSON.stringify(payload));
        const url = `${RECIPES_IFRAME_BASE_URL}?data=${encodedPayload}&personalized=true&embed=true`;
        console.log('🌐 Setting iframe URL with embed mode:', url);
        setIframeSrc(url);
        // Set initial referer to the constructed iframe URL
        setCurrentReferer(url);
        console.log('✅ Iframe src and referer set successfully');
        setDataError(null);
      } catch (e) {
        console.error('Error processing data for iframe:', e);
        setDataError('An error occurred while preparing recipes. Please try again.');
        setIframeSrc(null);
      }
    } else {
      let missingDataReason = 'Required data for recipe personalization is not available.';
      if (!userDetails || !userDetails?.userID) missingDataReason = 'User information not available.';
      else if (!allFoods) missingDataReason = 'Food data not available.';
      else if (!allRanks) missingDataReason = 'Rank data not available.';
      else if (!bootupState) missingDataReason = 'Bootup data (for timestamp) not available.';
      console.warn(missingDataReason);
      setDataError(missingDataReason);
      setIframeSrc(null);
    }
  }, [
    allFoods, allRanks, bootupState, userDetails, 
    isLoadingFoods, isLoadingRanks, isLoadingBootup, 
    errorFoods, errorRanks, isErrorBootup
  ]);

  // Header component for all states
  const renderHeader = () => (
    <div className={`bg-white px-4 py-3 ${iframeSrc ? '' : 'border-b border-gray-200'}`}>
      <button
        onClick={handleBackNavigation}
        className="flex items-center text-primary hover:opacity-70 transition-opacity p-0 md:p-2"
        aria-label="Go back"
      >
        <ChevronLeftIcon size={24} />
      </button>
    </div>
  );

  if (isLoadingFoods || isLoadingRanks || isLoadingBootup) {
    return (
      <div className="w-full h-full flex flex-col">
        {renderHeader()}
        <div className="p-4 text-center flex-grow flex items-center justify-center">
          Loading Personalized Recipes...
        </div>
      </div>
    ); 
  }

  if (dataError) {
    return (
      <div className="w-full h-full flex flex-col">
        {renderHeader()}
        <div className="p-4 text-red-500 text-center flex-grow flex items-center justify-center">
          Error: {dataError}
        </div>
      </div>
    ); 
  }

  if (iframeSrc) {
    return (
      <div className="w-full h-full flex flex-col">
        {renderHeader()}
        {/* The parent div in DashboardLayout is flex flex-1 flex-col. */}
        {/* This div should expand to fill the space given to Outlet. */}
        {/* The iframe then takes h-full of this div. */}
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title="Viome Recipes"
          className="w-full h-full border-none flex-grow"
          allow="clipboard-write"
          onLoad={handleIframeLoad}
          onError={() => {
            console.log('❌ Iframe failed to load');
            setIsIframeLoaded(false);
          }}
        />
      </div>
    );
  }

  // Fallback or initial state before URL is ready
  return (
    <div className="w-full h-full flex flex-col">
      {renderHeader()}
      <div className="p-4 text-center flex-grow flex items-center justify-center">
        Preparing your recipes...
      </div>
    </div>
  );
};

// It's good practice to export default if it's a screen/page component
export default RecipesPage; 